<template>
  <div>
    <HeadingSection
      class="mx-[24px] mb-[35px] sm:mx-0 sm:px-0"
      heading="API Access"
      desc="Tokens for the GPX public API. A token has full access to this account."
      :button="topButton"
    />
    <DataTable
      v-if="!isEmpty"
      :loading="isLoading"
      :list="pageRows"
      :columns="columns"
      :edit-actions="editActions"
    />
    <DataTableNav v-if="!isEmpty" v-model="pagination" :meta="paginationMeta" />
    <EmptyState
      v-if="isEmpty"
      class="mt-[35px]"
      type="table"
      heading="There are no tokens yet"
      desc="Create one to call the GPX public API from your own systems."
    />
    <p class="mt-[24px] text-sm text-gpx_gray-200">
      The token is shown once, when it is created. Revoking a token stops it
      working immediately.
      <a
        href="https://api-docs.gpx.co"
        target="_blank"
        rel="noopener"
        class="font-medium text-gpx_porto-300"
        >API documentation</a
      >
    </p>
  </div>
</template>

<script setup>
import { computed, ref, toRef } from 'vue';
import { useStore } from 'vuex';
import {
  getAccountPublicApiTokens,
  revokeAccountPublicApiToken,
} from '@gpx-spa/queries';
import DataTable from '@gpx-spa/ui/src/elements/DataTable.vue';
import DataTableNav from '@gpx-spa/ui/src/elements/DataTableNav.vue';
import HeadingSection from '@gpx-spa/ui/src/elements/HeadingSection.vue';
import EmptyState from '@gpx-spa/ui/src/components/EmptyState.vue';
import { TableResultsPerPage } from '@gpx-spa/constants';
import { formatTime, formatTimeAgo } from '@/filters';

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const { data, isLoading } = getAccountPublicApiTokens(
  toRef(props, 'accountId'),
);
const revokeMutation = revokeAccountPublicApiToken();

const tokens = computed(() => data.value?.data || []);

const columns = ['Name', 'Token', 'Created', 'Expires', 'Last used'];

const formatDate = (value) => formatTime(value, 'MMM d, yyyy');

const formatExpiry = (token) => {
  if (!token.expires_at) return 'Never';
  const expired = new Date(token.expires_at) <= new Date();
  return expired
    ? `Expired ${formatDate(token.expires_at)}`
    : formatDate(token.expires_at);
};

const rows = computed(() =>
  tokens.value.map((token) => ({
    id: token.id,
    name: token.name,
    token: `gpx_live_••••${token.last_four}`,
    created: formatDate(token.created_at),
    expires: formatExpiry(token),
    last_used: token.last_used_at ? formatTimeAgo(token.last_used_at) : 'Never',
    edit: true,
  })),
);

const isEmpty = computed(() => rows.value.length === 0 && !isLoading.value);

// The endpoint returns every token in one payload, so paging happens here — and the nav is also
// what closes the table off, the same as the other Manage tables.
const pagination = ref({ page: 1, per_page: TableResultsPerPage[0] });

const paginationMeta = computed(() => {
  const total = rows.value.length;
  const { page, per_page } = pagination.value;

  return {
    current_page: page,
    last_page: Math.max(1, Math.ceil(total / per_page)),
    per_page,
    from: total === 0 ? 0 : (page - 1) * per_page + 1,
    to: Math.min(page * per_page, total),
    total,
  };
});

const pageRows = computed(() => {
  const { page, per_page } = pagination.value;
  return rows.value.slice((page - 1) * per_page, page * per_page);
});

const topButton = computed(() => ({
  label: 'Create token',
  action: { mode: 'callback', callback: openCreate },
}));

const openCreate = () => {
  store.dispatch('modal/open', { name: 'ManageApiTokenCreateModal' });
};

const openRevoke = (id) => {
  const token = tokens.value.find((item) => item.id === id);
  if (!token) return;

  store.dispatch('modal/open', {
    name: 'CommonConfirmationModal',
    params: {
      actionLabel: 'Revoke token',
      title: token.name,
      body: 'Anything calling the API with this token stops working immediately. This cannot be undone — a replacement has to be created.',
      successMessage: 'Token revoked',
      errorMessage: 'Failed to revoke the token.',
      onOk: () =>
        revokeMutation.mutateAsync({
          accountId: props.accountId,
          id: token.id,
        }),
    },
  });
};

const editActions = [
  {
    icon: 'XMarkIcon',
    label: 'Revoke token',
    action: { mode: 'fn', fn: openRevoke },
  },
];
</script>
